import * as React from 'react';
import * as cx from 'classnames';
import { Dimmer, Icon, Loader } from 'semantic-ui-react';
import { DynamicModuleLoader, IModuleStore, IModule } from 'redux-dynamic-modules';
import { ISagaModule } from 'redux-dynamic-modules-saga';

import { SpeakingQuestionSetIds } from 'grading/src/services/speakingQuestionSetService';
import newAuthorUIService from 'author/src/services/newAuthorUIService';
import { FeatureHeaderType, Validator } from './typings';
import { ContextPropertyNames } from './featureProperties';

import FeatureStatusIndicator from '../components/featureStatusIndicator/FeatureStatusIndicator';
import { FeatureOptions } from '../types/FeatureTypes';
import { WorkspacesState } from '../store/workspaces/reducer';
import { setContextRequiredProperties } from '../store/workspaces/actions';
import { CommonContextState, ContextProperties } from '../store/context/types';
import { loadMissingProperties, refreshMissingProperties } from '../store/context/actions';

import { tl } from '../services/i18nService';
import { FeatureNamespace } from '../services/featureService';
import dynamicFetchFeatureComponent from '../services/ComponentLoaderService';
import arrayUtil from '../util/array/arrayUtil';

import * as styles from './FeatureLoader.scss';
import ComponentErrorBoundary from '../components/ComponentErrorBoundary';
import { ActionStatusService } from '../components/contextMenu/interfaces';

interface FeatureLoadedProps {
    onSetFeatureLoaded: (featureId: string) => void;
    featureId: string;
}

const FeatureLoaded: React.FC<FeatureLoadedProps> = ({ onSetFeatureLoaded, featureId }: FeatureLoadedProps) => {
    React.useEffect(() => {
        onSetFeatureLoaded(featureId);
    }, []);
    return null;
};

interface FeatureLoaderInputProps {
    featureName: string;
    appName?: string;
    options: FeatureOptions;
    footerMode?: boolean;
    headerMode?: boolean;
    useFullScreen?: boolean;
    context: CommonContextState;
    workspaces?: WorkspacesState;
    setFeatureLoaded: (featureId: string) => void;
    featureIsExpandedMode?: boolean;
    isFeatureLoading: (featureId: string) => boolean;
    getGenericFeatureState: (featureId: string) => any;
    setupGenericFeatureState: (featureId: string) => void;
    activeOverlay: string;
    featureId: string;
    usePlagiarismService?: boolean;
    language: string;
    appStore: IModuleStore<any>;
    featureNamespace: FeatureNamespace;
    FeatureHeader: FeatureHeaderType;
    actionStatusService: ActionStatusService;
    viewAsStudent?: boolean;
    disableFeatureWrapperStyles?: boolean;
    viewRubricMarksInStudentReport?: boolean;
    enableWorkspaceBreadcrumbs?: boolean;
    upwardDropdown?: boolean;
    headerWidgets?: string[];
}

export interface FeatureProps extends FeatureLoaderInputProps {
    children?: React.ReactNode;
}

interface FeatureState {
    // TODO find some suitable typings here
    featureContextMenu: any;
    featureComponent: any;
    inErrorState: boolean;
    requiredContextProperties: ContextPropertyNames | null;
    featureModule: ISagaModule<any> | IModule<any>;
    isMounted: boolean;
}


const positiveInt: Validator = (val: number) => val > 0;
const stringWithLength: Validator = (val: string) => typeof val === 'string' && val.length >= 1 && val !== '-';
const partyInfoValidator = (partyInfo: ContextProperties): boolean => {
    return stringWithLength(partyInfo.partyName);
};
const questionInfoValidator = (questionInfo: ContextProperties): boolean => {
    return stringWithLength(questionInfo.questionNumbering) && positiveInt(questionInfo.questionNumber)
        && stringWithLength(questionInfo.questionType);
};
const assessmentRunSessionValidator = (assessmentRunSession: ContextProperties): boolean => {
    return positiveInt(assessmentRunSession?.assessmentRunSessionId);
};
const speakingQuestionSetIdsValidator = (speakingQuestionSetIds: SpeakingQuestionSetIds): boolean => {
    return positiveInt(speakingQuestionSetIds?.part1SectionId)
        && positiveInt(speakingQuestionSetIds?.part2And3SectionId)
        && positiveInt(speakingQuestionSetIds?.part2QuestionId)
        && positiveInt(speakingQuestionSetIds?.part3QuestionId);
};
const contextValidators: { [index: string]: Validator } = {
    assessmentRunId: positiveInt,
    workspaceId: stringWithLength,
    partyId: positiveInt,
    questionId: positiveInt,
    partyState: partyInfoValidator,
    questionState: questionInfoValidator,
    assessmentRunSession: assessmentRunSessionValidator,
    speakingQuestionSetIds: speakingQuestionSetIdsValidator,
    contentItemId: positiveInt,
};

const validateContextProperty = (propertyName: string, context: CommonContextState): boolean => {
    const validator: Validator = contextValidators[propertyName];
    if (validator !== undefined) {
        const contextValue: any = context[propertyName];
        return validator(contextValue);
    }

    return false;
};

class FeatureLoader extends React.Component<FeatureLoaderInputProps, FeatureState> {
    requestedContextProperties: ContextPropertyNames = [];
    asyncFeatureComponent: any;
    constructor(props: FeatureLoaderInputProps) {
        super(props);

        if (!props.footerMode && !props.useFullScreen) {
            props.setupGenericFeatureState(props.featureId);
        }

        this.state = {
            featureContextMenu: null,
            featureComponent: null,
            inErrorState: null,
            requiredContextProperties: null,
            featureModule: null,
            isMounted: false,
        };
    }

    componentDidMount(): void {
        const {
            featureName, context, featureId, options, featureNamespace, appStore,
        } = this.props;
        this.setState({ isMounted: true });
        this.asyncFeatureComponent = dynamicFetchFeatureComponent(featureNamespace, featureName);
        if (this.asyncFeatureComponent) {
            this.asyncFeatureComponent.load().then((feature: any) => {
                const {
                    component, getFeatureContextMenu, getModule, getRequiredContextProperties,
                } = feature;
                let featureContextMenu;
                let requiredContextProperties;
                let featureModule;
                if (typeof getModule === 'function') {
                    featureModule = getModule(featureId, options);
                }

                if (typeof getFeatureContextMenu === 'function') {
                    featureContextMenu = getFeatureContextMenu(featureId, context);
                }

                if (typeof getRequiredContextProperties === 'function') {
                    requiredContextProperties = getRequiredContextProperties(options);
                }

                appStore.dispatch(setContextRequiredProperties(requiredContextProperties));

                this.setState({
                    featureContextMenu,
                    requiredContextProperties,
                    featureModule,
                    featureComponent: component,
                });
            }).catch((error: any) => {
                if (this.state.isMounted) {
                    console.error('Error loading async module', error);
                }
                this.setState({
                    inErrorState: true,
                });
            });
        }
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillUpdate(nextProps: Readonly<FeatureLoaderInputProps>, nextState: Readonly<FeatureState>): void {
        this.requestMissingContextProperties(nextProps.context, nextState.requiredContextProperties);
    }

    componentDidUpdate(prevProps: FeatureLoaderInputProps): void {
        const { context, appStore, featureName } = this.props;
        const { questionId, partyId, partyState } = context;
        const { requiredContextProperties } = this.state;
        if (requiredContextProperties) {
            if (requiredContextProperties.includes('partyState')
                && (prevProps.context.partyId !== partyId
                    // Fix for https://inspera.atlassian.net/browse/IA3-2902
                    || (featureName === 'submission' && partyState.partyId !== partyId))) {
                appStore.dispatch(refreshMissingProperties(partyId, ['partyState']));
            }

            if ((prevProps.context.questionId !== questionId)
                && requiredContextProperties.includes('questionState')) {
                appStore.dispatch(refreshMissingProperties(questionId, ['questionState']));
            }
        }
    }

    componentWillUnmount(): void {
        this.setState({ isMounted: false });
        this.asyncFeatureComponent.cancel();
    }

    componentDidCatch(error: Error, info: React.ErrorInfo): void {
        console.error('FeatureLoader did catch error', error, info);
        this.setState({
            inErrorState: true,
        });
    }

    requestMissingContextProperties(context: CommonContextState, requiredContextProperties: ContextPropertyNames): void {
        if (!Array.isArray(requiredContextProperties)
            || arrayUtil.includesAll(this.requestedContextProperties, requiredContextProperties)) {
            return;
        }

        const missingProperties = requiredContextProperties
            .filter((propertyName: string) => !validateContextProperty(propertyName, context));

        if (missingProperties.length > 0) {
            this.props.appStore.dispatch(loadMissingProperties(missingProperties));
        }

        this.requestedContextProperties = arrayUtil.pushUnique(this.requestedContextProperties, missingProperties);
    }

    isContextValid(context: CommonContextState, requiredContextProperties: ContextPropertyNames = []): boolean {
        if (requiredContextProperties === null) {
            return false;
        }

        return requiredContextProperties
            .filter((propertyName: string) => {
                if (context?.questionState?.noQuestionFound && (propertyName === 'questionId' || propertyName === 'questionState')) {
                    return false;
                }
                return true;
            })
            .every((propertyName: string) => validateContextProperty(propertyName, context));
    }

    renderFeatureComponent(): React.ReactElement {
        const { featureId, setFeatureLoaded } = this.props;
        if (this.state.featureComponent) {
            // eslint-disable-next-line react/jsx-props-no-spreading
            return (
                <DynamicModuleLoader modules={this.state.featureModule ? [this.state.featureModule] : []}>
                    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                    <FeatureLoaded onSetFeatureLoaded={setFeatureLoaded} featureId={featureId} />
                    <ComponentErrorBoundary><this.state.featureComponent {...this.props} /></ComponentErrorBoundary>
                </DynamicModuleLoader>
            );
        }
        if (this.state && this.state.inErrorState) {
            return <Icon name="bug" />;
        }
        return <Loader active inline="centered" />;
    }

    render(): React.ReactElement {
        const {
            FeatureHeader, featureName, featureId, footerMode,
            context, options, headerMode, activeOverlay, appStore,
            featureNamespace, actionStatusService, disableFeatureWrapperStyles, setFeatureLoaded,
        } = this.props;
        const { requiredContextProperties } = this.state;
        const { featureContextMenu } = this.state;
        const globalState = appStore.getState();
        const { showHeader } = this.props.getGenericFeatureState(featureId);
        const contextReady = (options && options.skipContext) || this.isContextValid(context, requiredContextProperties);
        const featureState = globalState && globalState[featureId];
        const flagged = featureState && featureState.score && featureState.score.flagged;
        const tableContainerClass = featureName === 'table' && !activeOverlay ? 'tableContainer' : '';
        const titleCaptionValue = { partyName: context.partyState?.partyName };
        const title = options && options.titleCaptionKey !== undefined
            ? tl(options.titleCaptionKey, titleCaptionValue)
            : tl(`grading.${featureName}.title`, titleCaptionValue);

        if (options?.marketplaceDependencies && Object.keys(options.marketplaceDependencies).length > 0) {
            const marketplaceDependenciesMet = Object.entries(options.marketplaceDependencies).every(([featureDependency, requiredValue]) => {
                const dependencyValue = globalState.assessment[featureDependency];
                return dependencyValue === requiredValue;
            });
            if (!marketplaceDependenciesMet) {
                return <FeatureLoaded onSetFeatureLoaded={setFeatureLoaded} featureId={featureId} />;
            }
        }

        if (footerMode) {
            return contextReady ? this.renderFeatureComponent() : null;
        }

        if (headerMode) {
            const isSimpleHeader = !options;
            return (
                <div className={`featureWrapper ${isSimpleHeader ? 'simpleHeader' : styles.workspaceHeader}`}>
                    {contextReady ? this.renderFeatureComponent() : null}
                    <FeatureHeader
                        title={title}
                        featureContextMenu={featureContextMenu}
                        promotedActions={options && options.promotedActions}
                        showHeader={showHeader}
                        withHeader={options && options.withHeader}
                        featureName={featureName}
                        featureId={featureId}
                        headerMode
                        featureNamespace={featureNamespace}
                        appStore={appStore}
                        hideContextMenu={options && options.hideContextMenu}
                        actionStatusService={actionStatusService}
                    />
                </div>
            );
        }

        const fullHeight = featureName === 'submission' || (options && options.fullHeight);
        const isDetailsPanel = featureName === 'detailsPanel';
        const isMajorVersions = featureName === 'majorVersions';

        return (
            <div
                className={cx(
                    (!isDetailsPanel && !disableFeatureWrapperStyles && !isMajorVersions ? 'featureWrapper' : ''),
                    (fullHeight ? styles.fullHeight : ''),
                    (showHeader && options && (options.withHeader || options.contextMenuAboveFeature) ? styles.showHeader : ''),
                    (flagged ? styles.flagged : ''),
                    (featureName === 'submission' ? styles.fixHeight : ''),
                    (newAuthorUIService.isNewAuthorUIEnabled() ? styles.noPadding : ''),
                )}
            >
                {!isDetailsPanel && !isMajorVersions && <FeatureStatusIndicator featureId={featureId} />}
                <FeatureHeader
                    title={title}
                    featureContextMenu={featureContextMenu}
                    promotedActions={options && options.promotedActions}
                    showHeader={showHeader}
                    withHeader={options && options.withHeader}
                    featureName={featureName}
                    featureId={featureId}
                    featureNamespace={featureNamespace}
                    appStore={appStore}
                    hideContextMenu={options && options.hideContextMenu}
                    actionStatusService={actionStatusService}
                    contextMenuAlwaysActive={options && options.contextMenuAlwaysActive}
                    contextMenuAboveFeature={options && options.contextMenuAboveFeature}
                />
                <Dimmer.Dimmable
                    className={`${tableContainerClass} ${featureName === 'table' && !activeOverlay ? styles.responsiveTableContainer : ''}`}
                >
                    {
                        contextReady
                            ? this.renderFeatureComponent()
                            : null
                    }
                </Dimmer.Dimmable>
            </div>
        );
    }
}

export default FeatureLoader;
